/**
 * DPDP Compliant Multilingual Terms & Conditions Content
 * Supports English and Hindi
 */

export const termsConditionsContent = {
    en: {
        title: "Terms and Conditions",
        intro: {
            p1: "Welcome to Eternal Locker™. By accessing or using our services, you agree to be bound by the following terms and conditions. Please read them carefully before using the platform.",
            p2: "For information about how we handle your personal data, please review our Privacy Policy."
        },
        sections: {
            1: {
                title: "1. Acceptance of Terms",
                content: "By registering, accessing, or using Eternal Locker™, you acknowledge that you have read, understood, and agree to these Terms and Conditions, our Privacy Policy, and any other policies referenced herein."
            },
            2: {
                title: "2. User Responsibilities",
                content: [
                    "You must provide accurate and complete information during registration and keep your account details up to date.",
                    "You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account.",
                    "You agree not to use Eternal Locker™ for any unlawful, harmful, or fraudulent activities."
                ]
            },
            3: {
                title: "3. Data Security and Privacy",
                content: [
                    "We implement industry-standard security measures to protect your data. However, no method of transmission or storage is 100% secure.",
                    "Your personal information is handled in accordance with our Privacy Policy."
                ]
            },
            4: {
                title: "4. Subscription and Payments",
                content: [
                    "Access to Eternal Locker™ requires a valid subscription. Payment terms, renewal, and cancellation policies are described on our website and during the subscription process.",
                    "All payments are processed securely via third-party payment providers."
                ]
            },
            5: {
                title: "5. Content Ownership",
                content: [
                    "You retain ownership of the files and information you upload to Eternal Locker™.",
                    "By uploading content and data, you grant us a limited license to store, process, and display your data as necessary to provide our services."
                ]
            },
            6: {
                title: "6. Nominee Designation and Access",
                content: [
                    "You may designate one or more nominees. By doing so you instruct us to grant those nominees read access to your locker if your account becomes inactive, and you confirm that you have obtained the nominee's details lawfully.",
                    "Nominee access is triggered by inactivity alone - 20 days without a login - and not by any evidence of death or incapacity. We do not verify whether a nominee is entitled to your estate. Section 3 of our Privacy Policy describes the full process, including the reminder emails we send you before access is granted and how to stop it.",
                    "Nominee access is a custodial read grant for the purpose of locating and preserving your records. It does not transfer ownership of, or any beneficial interest in, anything described in your locker, and it creates no right of inheritance or succession. Under Indian law a nominee is not a successor: a nominee who accesses your locker holds any information obtained for the benefit of your legal heirs, and the devolution of your assets continues to be governed by your will or by the succession law that applies to you.",
                    "You remain responsible for keeping your nominee list current, and you may add or remove nominees at any time while your account is active.",
                    "If nominee access is granted in error - for example because you were unable to read our reminder emails - contact us immediately and we will revoke it."
                ]
            },
            7: {
                title: "7. Account Termination",
                content: [
                    "You may terminate your account at any time. Upon termination, your data will be deleted in accordance with our data retention policy.",
                    "We reserve the right to suspend or terminate accounts that violate these Terms or engage in suspicious activity."
                ]
            },
            8: {
                title: "8. Limitation of Liability",
                content: [
                    "Eternal Locker™ is provided \"as is\" without warranties of any kind. We are not liable for any damages resulting from the use or inability to use our services.",
                    "We do not guarantee uninterrupted access, data accuracy, or error-free operation."
                ]
            },
            9: {
                title: "9. Changes to Terms",
                content: "We may update these Terms and Conditions from time to time. Changes will be posted with an updated effective date, and where required by applicable law we will request explicit re-acceptance before materially revised terms apply."
            },
            10: {
                title: "10. Contact Us",
                content: "If you have any questions or concerns about these Terms and Conditions, please contact us at {{SUPPORT_EMAIL}}. For privacy matters, see our Privacy Policy. For grievance-specific matters, see our Grievance Redressal page."
            },
            11: {
                title: "11. Compliance and Applicable Law",
                content: [
                    "We operate our services in alignment with applicable legal and regulatory requirements, including privacy and consumer protection obligations.",
                    "Your statutory rights, where applicable, remain unaffected by these Terms.",
                    "Compliance-related concerns may also be raised through our Grievance Redressal channel."
                ]
            }
        }
    },
    hi: {
        title: "नियम और शर्तें",
        intro: {
            p1: "Eternal Locker™ में स्वागत है। हमारी सेवाओं तक पहुंचने या उपयोग करके, आप निम्नलिखित नियमों और शर्तों से बंधे होने के लिए सहमत हैं। प्लेटफ़ॉर्म का उपयोग करने से पहले कृपया उन्हें ध्यान से पढ़ें।",
            p2: "हम आपकी व्यक्तिगत जानकारी को कैसे संभालते हैं, इसके बारे में जानकारी के लिए, कृपया हमारी गोपनीयता नीति देखें।"
        },
        sections: {
            1: {
                title: "1. शर्तों की स्वीकृति",
                content: "Eternal Locker™ में पंजीकरण करके, पहुंचकर या उपयोग करके, आप स्वीकार करते हैं कि आपने इन नियमों और शर्तों, हमारी गोपनीयता नीति और यहां संदर्भित किसी भी अन्य नीति को पढ़ा, समझा और सहमत हैं।"
            },
            2: {
                title: "2. उपयोगकर्ता की जिम्मेदारियां",
                content: [
                    "आपको पंजीकरण के दौरान सटीक और पूर्ण जानकारी प्रदान करनी चाहिए और अपने खाते की जानकारी को अद्यतन रखना चाहिए।",
                    "आप अपने खाते की साख को गोपनीय रखने और आपके खाते के तहत होने वाली सभी गतिविधियों के लिए जिम्मेदार हैं।",
                    "आप Eternal Locker™ का उपयोग किसी भी अवैध, हानिकारक या धोखाधड़ी की गतिविधियों के लिए नहीं करने के लिए सहमत हैं।"
                ]
            },
            3: {
                title: "3. डेटा सुरक्षा और गोपनीयता",
                content: [
                    "हम आपके डेटा की सुरक्षा के लिए उद्योग-मानक सुरक्षा उपायों को लागू करते हैं। हालांकि, कोई भी संचरण या भंडारण की विधि 100% सुरक्षित नहीं है।",
                    "आपकी व्यक्तिगत जानकारी हमारी गोपनीयता नीति के अनुसार संभाली जाती है।"
                ]
            },
            4: {
                title: "4. सदस्यता और भुगतान",
                content: [
                    "Eternal Locker™ तक पहुंच के लिए एक वैध सदस्यता की आवश्यकता है। भुगतान शर्तें, नवीनीकरण और रद्दीकरण नीतियां हमारी वेबसाइट पर और सदस्यता प्रक्रिया के दौरान वर्णित हैं।",
                    "सभी भुगतान तीसरे पक्ष के भुगतान प्रदाताओं के माध्यम से सुरक्षित रूप से संसाधित किए जाते हैं।"
                ]
            },
            5: {
                title: "5. कंटेंट स्वामित्व",
                content: [
                    "आप Eternal Locker™ में अपलोड की गई फाइलों और जानकारी के स्वामित्व को बनाए रखते हैं।",
                    "कंटेंट और डेटा अपलोड करके, आप हमें हमारी सेवाएं प्रदान करने के लिए आवश्यक रूप से आपके डेटा को संग्रहीत, संसाधित और प्रदर्शित करने के लिए एक सीमित लाइसेंस देते हैं।"
                ]
            },
            6: {
                title: "6. नॉमिनी नामांकन और पहुंच",
                content: [
                    "आप एक या अधिक नॉमिनी नामित कर सकते हैं। ऐसा करके आप हमें निर्देश देते हैं कि यदि आपका खाता निष्क्रिय हो जाए तो उन नॉमिनी को आपके लॉकर तक पढ़ने की पहुंच दी जाए, और आप पुष्टि करते हैं कि आपने नॉमिनी का विवरण वैध रूप से प्राप्त किया है।",
                    "नॉमिनी पहुंच केवल निष्क्रियता से शुरू होती है - 20 दिनों तक लॉगिन न होने से - न कि मृत्यु या अक्षमता के किसी प्रमाण से। हम यह सत्यापित नहीं करते कि नॉमिनी आपकी संपत्ति का हकदार है या नहीं। हमारी गोपनीयता नीति की धारा 3 में पूरी प्रक्रिया वर्णित है, जिसमें पहुंच देने से पहले भेजे जाने वाले अनुस्मारक ईमेल और इसे रोकने का तरीका शामिल है।",
                    "नॉमिनी पहुंच आपके रिकॉर्ड खोजने और सुरक्षित रखने के उद्देश्य से दी गई एक अभिरक्षा पहुंच है। यह आपके लॉकर में वर्णित किसी भी वस्तु का स्वामित्व या कोई हितलाभ हस्तांतरित नहीं करती, और कोई उत्तराधिकार अधिकार उत्पन्न नहीं करती। भारतीय कानून के अनुसार नॉमिनी उत्तराधिकारी नहीं होता: आपके लॉकर तक पहुंचने वाला नॉमिनी प्राप्त जानकारी को आपके कानूनी उत्तराधिकारियों के लाभ के लिए धारण करता है, और आपकी संपत्ति का हस्तांतरण आपकी वसीयत या आप पर लागू उत्तराधिकार कानून द्वारा ही शासित रहता है।",
                    "अपनी नॉमिनी सूची को अद्यतन रखने की जिम्मेदारी आपकी है, और जब तक आपका खाता सक्रिय है आप किसी भी समय नॉमिनी जोड़ या हटा सकते हैं।",
                    "यदि नॉमिनी पहुंच गलती से दी गई हो - उदाहरण के लिए इसलिए कि आप हमारे अनुस्मारक ईमेल नहीं पढ़ सके - तो तुरंत हमसे संपर्क करें और हम उसे रद्द कर देंगे।"
                ]
            },
            7: {
                title: "7. खाता समाप्ति",
                content: [
                    "आप किसी भी समय अपने खाते को समाप्त कर सकते हैं। समाप्ति के बाद, आपका डेटा हमारी डेटा प्रतिधारण नीति के अनुसार हटा दिया जाएगा।",
                    "हम इन शर्तों का उल्लंघन करने वाले या संदिग्ध गतिविधि में संलग्न रहने वाले खातों को निलंबित या समाप्त करने का अधिकार सुरक्षित रखते हैं।"
                ]
            },
            8: {
                title: "8. दायित्व की सीमा",
                content: [
                    "Eternal Locker™ को \"जैसा है\" प्रदान किया जाता है बिना किसी प्रकार की वारंटी के। हम हमारी सेवाओं के उपयोग या उपयोग में असमर्थता से उत्पन्न होने वाली किसी भी क्षति के लिए कोई दायित्व नहीं हैं।",
                    "हम निरंतर पहुंच, डेटा सटीकता या त्रुटि-मुक्त संचालन की गारंटी नहीं देते हैं।"
                ]
            },
            9: {
                title: "9. शर्तों में परिवर्तन",
                content: "हम समय-समय पर इन शर्तों और शर्तों को अपडेट कर सकते हैं। परिवर्तन एक अद्यतन प्रभावी तारीख के साथ पोस्ट किए जाएंगे, और जहां लागू कानून द्वारा आवश्यक हो, हम भौतिक रूप से संशोधित शर्तें लागू होने से पहले स्पष्ट पुनः स्वीकृति का अनुरोध करेंगे।"
            },
            10: {
                title: "10. हमसे संपर्क करें",
                content: "यदि आपके पास इन शर्तों और शर्तों के बारे में कोई सवाल या चिंता है, तो कृपया हमसे {{SUPPORT_EMAIL}} पर संपर्क करें। गोपनीयता संबंधी मामलों के लिए, हमारी गोपनीयता नीति देखें। शिकायत-विशिष्ट मामलों के लिए, हमारे शिकायत निवारण पृष्ठ देखें।"
            },
            11: {
                title: "11. अनुपालन और लागू कानून",
                content: [
                    "हम अपनी सेवाएं लागू कानूनी और नियामक आवश्यकताओं के अनुसार संचालित करते हैं, जिनमें गोपनीयता और उपभोक्ता सुरक्षा दायित्व शामिल हैं।",
                    "आपके सांविधिक अधिकार, जहां लागू हैं, इन शर्तों से प्रभावित नहीं रहते हैं।",
                    "अनुपालन-संबंधी चिंताओं को हमारे शिकायत निवारण चैनल के माध्यम से भी उठाया जा सकता है।"
                ]
            }
        }
    }
};

export function getTermsConditionsContent(lang = 'en') {
    return termsConditionsContent[lang] || termsConditionsContent.en;
}

export function getAvailableLanguagesForTerms() {
    return Object.keys(termsConditionsContent);
}
